import {NavbarProps} from '@common/ui/navigation/navbar/navbar';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {Button} from '@ui/buttons/button';
import {ButtonColor} from '@ui/buttons/get-shared-button-style';
import {IconButton} from '@ui/buttons/icon-button';
import {Item} from '@ui/forms/listbox/item';
import {Trans} from '@ui/i18n/trans';
import {PersonIcon} from '@ui/icons/material/Person';
import {Menu, MenuTrigger} from '@ui/menu/menu-trigger';
import {useSettings} from '@ui/settings/use-settings';
import {Fragment} from 'react';
import {Link} from 'react-router';

interface NavbarAuthButtonsProps {
  primaryButtonColor?: ButtonColor;
  navbarColor?: NavbarProps['color'];
  variant?: 'default' | 'playerGuest' | 'pricingGuest';
}
export function NavbarAuthButtons({
  primaryButtonColor,
  navbarColor,
  variant = 'default',
}: NavbarAuthButtonsProps) {
  if (!primaryButtonColor) {
    primaryButtonColor = navbarColor === 'primary' ? 'elevated' : 'primary';
  }

  return (
    <Fragment>
      <MobileButtons variant={variant} />
      <DesktopButtons
        primaryButtonColor={primaryButtonColor}
        variant={variant}
      />
    </Fragment>
  );
}

interface DesktopButtonsProps {
  primaryButtonColor: ButtonColor;
  variant: NonNullable<NavbarAuthButtonsProps['variant']>;
}
function DesktopButtons({primaryButtonColor, variant}: DesktopButtonsProps) {
  const {registration} = useSettings();

  if (variant === 'playerGuest') {
    return (
      <div className="flex items-center gap-16 max-md:hidden">
        <Button
          elementType={Link}
          to="/pricing"
          variant="outline"
          color="white"
          radius="rounded-full"
          sizeClassName="h-48 min-w-112 px-20 text-base"
          className="!inline-flex !items-center justify-center !border-white/25 !bg-transparent !py-0 !leading-none !text-white hover:!border-white/40 hover:!bg-white/10"
        >
          <span className="relative -top-[2px]">Cennik</span>
        </Button>
        <Button
          elementType={Link}
          to="/login"
          variant="outline"
          color="white"
          radius="rounded-full"
          sizeClassName="h-48 min-w-144 px-24 text-base"
          className="!inline-flex !items-center justify-center !border-white/25 !bg-transparent !py-0 !leading-none !text-white hover:!border-white/40 hover:!bg-white/10"
        >
          <span className="relative -top-[2px]">
            <Trans message="Login" />
          </span>
        </Button>
        {!registration?.disable && (
          <Button
            elementType={Link}
            to="/register"
            variant="raised"
            radius="rounded-full"
            sizeClassName="h-48 min-w-224 px-24 text-base"
            className="!inline-flex !items-center justify-center !border-[#dfff00] !bg-[#dfff00] !py-0 !leading-none !text-black shadow-none hover:!border-[#e8ff40] hover:!bg-[#e8ff40]"
          >
            <span className="relative -top-[2px]">Przetestuj za darmo</span>
          </Button>
        )}
      </div>
    );
  }

  if (variant === 'pricingGuest') {
    return (
      <div className="text-sm max-md:hidden">
        <Button
          elementType={Link}
          to="/pricing"
          variant="text"
          className="mr-10"
        >
          Cennik
        </Button>
        <Button elementType={Link} to="/login" variant="text" className="mr-10">
          <Trans message="Login" />
        </Button>
        {!registration?.disable && (
          <Button
            elementType={Link}
            to="/register"
            variant="raised"
            color={primaryButtonColor}
          >
            Wypróbuj za darmo
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="text-sm max-md:hidden">
      {!registration?.disable && (
        <Button
          elementType={Link}
          to="/register"
          variant="text"
          className="mr-10"
        >
          Wypróbuj za darmo
        </Button>
      )}
      <Button
        elementType={Link}
        to="/login"
        variant="raised"
        color={primaryButtonColor}
      >
        <Trans message="Login" />
      </Button>
    </div>
  );
}

function MobileButtons({
  variant,
}: {
  variant: NonNullable<NavbarAuthButtonsProps['variant']>;
}) {
  const {registration} = useSettings();
  const navigate = useNavigate();
  return (
    <MenuTrigger>
      <IconButton size="md" className="md:hidden">
        <PersonIcon />
      </IconButton>
      <Menu>
        {(variant === 'playerGuest' || variant === 'pricingGuest') && (
          <Item value="pricing" onSelected={() => navigate('/pricing')}>
            Cennik
          </Item>
        )}
        <Item value="login" onSelected={() => navigate('/login')}>
          <Trans message="Login" />
        </Item>
        {!registration?.disable && (
          <Item value="register" onSelected={() => navigate('/register')}>
            {variant === 'playerGuest'
              ? 'Przetestuj za darmo'
              : 'Wypróbuj za darmo'}
          </Item>
        )}
      </Menu>
    </MenuTrigger>
  );
}
